import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface Grower {
  grower_id:       string
  name:            string
  contact_name:    string | null
  contact_phone:   string | null
  contact_email:   string | null
  whatsapp_number: string | null
  address:         string | null
  notes:           string | null
  is_active:       boolean
  /** Mig 406 — bumped by `bump_version_trigger` on every real-field UPDATE. Pair with `updateWithVersion`. */
  version:         number
  // Optional per-grower bin tare override (mig 173). NULL = fall back to
  // system_config.bin_tare_kg. Used by the tipping_log net-weight trigger
  // and surfaced in the Rebin / Tipping UI for the operator's reference.
  bin_tare_kg:     number | null
  // Mig 428 — billing-identity + structured-address fields used on the
  // invoice letterhead. All optional. The structured address fields take
  // precedence over the legacy free-text `address` when any of line1 /
  // city / postcode is set.
  vat_number:           string | null
  ggn_number:           string | null
  company_registration: string | null
  address_line1:        string | null
  address_line2:        string | null
  city:                 string | null
  postcode:             string | null
  country:              string | null
  // Mig 20260814120000 — per-grower payment terms override in days; NULL =
  // the business default (packhouse_config.payment_terms_days). Packhouse-only:
  // no farm/override lane. Gated to Financial edit (F13 D3).
  payment_terms_days?:  number | null
  // Stage 1 of grower inheritance (mig 270/271). When this packhouse
  // grower is linked to a farm tenant, contact / address / notes /
  // bin_tare_kg are resolved from growers_resolved (COALESCE over
  // packhouse_override → farm_tenant_master → packhouse_fallback).
  // The fields above already hold the RESOLVED values; the metadata
  // below tells the UI where each value came from.
  linked_farm_tenant_id?:   string | null
  contact_name_source?:     'override' | 'farm' | 'packhouse'
  contact_phone_source?:    'override' | 'farm' | 'packhouse'
  contact_email_source?:    'override' | 'farm' | 'packhouse'
  whatsapp_number_source?:  'override' | 'farm' | 'packhouse'
  address_source?:          'override' | 'farm' | 'packhouse'
  notes_source?:            'override' | 'farm' | 'packhouse'
  bin_tare_kg_source?:      'override' | 'farm' | 'packhouse'
  // Source metadata for the mig-428 fields.
  vat_number_source?:           'override' | 'farm' | 'packhouse'
  ggn_number_source?:           'override' | 'farm' | 'packhouse'
  company_registration_source?: 'override' | 'farm' | 'packhouse'
  address_line1_source?:        'override' | 'farm' | 'packhouse'
  address_line2_source?:        'override' | 'farm' | 'packhouse'
  city_source?:                 'override' | 'farm' | 'packhouse'
  postcode_source?:             'override' | 'farm' | 'packhouse'
  country_source?:              'override' | 'farm' | 'packhouse'
  master_contact_name?:     string | null
  master_contact_phone?:    string | null
  master_contact_email?:    string | null
  master_whatsapp_number?:  string | null
  master_address?:          string | null
  master_notes?:            string | null
  master_bin_tare_kg?:      number | null
  // Master pass-throughs for the mig-428 fields.
  master_vat_number?:           string | null
  master_ggn_number?:           string | null
  master_company_registration?: string | null
  master_address_line1?:        string | null
  master_address_line2?:        string | null
  master_city?:                 string | null
  master_postcode?:             string | null
  master_country?:              string | null
  contact_name_override?:    string | null
  contact_phone_override?:   string | null
  contact_email_override?:   string | null
  whatsapp_number_override?: string | null
  address_override?:         string | null
  notes_override?:           string | null
  bin_tare_kg_override?:     number | null
  // Override pass-throughs for the mig-428 fields.
  vat_number_override?:           string | null
  ggn_number_override?:           string | null
  company_registration_override?: string | null
  address_line1_override?:        string | null
  address_line2_override?:        string | null
  city_override?:                 string | null
  postcode_override?:             string | null
  country_override?:              string | null
}

export interface Puc {
  puc_id:    string
  grower_id: string
  puc_code:  string
  farm_name: string | null
  /** Mig 358 — bumped by `bump_version_trigger` on every real-field UPDATE. Pair with `updateWithVersion`. */
  version:   number
  // Stage 3c grower inheritance (mig 273/274). When linked to a farm-tenant
  // master PUC, puc_code + farm_name are RESOLVED values from pucs_resolved.
  master_puc_id?:       string | null
  puc_code_source?:     'override' | 'farm' | 'packhouse'
  farm_name_source?:    'override' | 'farm' | 'packhouse'
  master_puc_code?:     string | null
  master_farm_name?:    string | null
  puc_code_override?:   string | null
  farm_name_override?:  string | null
}

export interface Block {
  block_id:             string
  puc_id:               string
  block_name:           string
  variety:              string | null
  commodity:            string | null
  notes:                string | null
  hectares:             number | null
  tree_count:           number | null
  planted_month:        string | null   // ISO date (first of month, e.g. 2018-03-01)
  is_bearing:           boolean         // true = orchard producing commercial fruit; false = still establishing
  /** From the base `blocks` row (blocks_resolved doesn't carry it). NULL reads as ORCHARD.
   *  The Growers page sets ORCHARD or FIELD ("Planting"); PIVOT/OTHER come from the farm app. */
  land_type?:           'ORCHARD' | 'PIVOT' | 'FIELD' | 'OTHER' | null
  /** Mig 358 — bumped by `bump_version_trigger` on every real-field UPDATE. Pair with `updateWithVersion`. */
  version:              number
  // Agronomy (mig 20260716152049). In blocks_resolved these are READ-ONLY
  // COALESCE(master, own) — deliberately no *_override / *_source companions
  // (farm-authored; the packhouse edits them only on UNLINKED blocks, where
  // the base column is authoritative).
  rootstock?:           string | null
  soil_type?:           string | null
  irrigation_type?:     string | null
  row_spacing_m?:       number | null
  tree_spacing_m?:      number | null
  trees_per_ha?:        number | null
  // Packhouse-side estimate (mig 20260806150000) — blocks_resolved exposes
  // the packhouse row's OWN value; never inherited from a farm master.
  est_harvest_start?:   string | null
  phytclean_cleared?:   boolean
  phytclean_checked_at?: string | null
  // Stage 3c grower inheritance (mig 273/274). When linked to a farm-tenant
  // master block, the eight inheritable fields are RESOLVED values from
  // blocks_resolved (COALESCE override → master → fallback).
  master_block_id?:           string | null
  block_name_source?:         'override' | 'farm' | 'packhouse'
  variety_source?:            'override' | 'farm' | 'packhouse'
  commodity_source?:          'override' | 'farm' | 'packhouse'
  hectares_source?:           'override' | 'farm' | 'packhouse'
  tree_count_source?:         'override' | 'farm' | 'packhouse'
  is_bearing_source?:         'override' | 'farm' | 'packhouse'
  planted_month_source?:      'override' | 'farm' | 'packhouse'
  notes_source?:              'override' | 'farm' | 'packhouse'
  master_block_name?:         string | null
  master_variety?:            string | null
  master_commodity?:          string | null
  master_hectares?:           number | null
  master_tree_count?:         number | null
  master_is_bearing?:         boolean | null
  master_planted_month?:      string | null
  master_notes?:              string | null
  block_name_override?:       string | null
  variety_override?:          string | null
  commodity_override?:        string | null
  hectares_override?:         number | null
  tree_count_override?:       number | null
  is_bearing_override?:       boolean | null
  planted_month_override?:    string | null
  notes_override?:            string | null
}

// All active growers (excludes soft-deleted via the admin delete flow).
//
// Reads from `growers_resolved` (mig 271) so consumers automatically get
// the COALESCE-resolved contact / address / notes / bin_tare_kg fields
// when the grower has a farm-tenant link, with the per-field source
// metadata available for any consumer that wants to render badges.
// growers_resolved only returns packhouse-owned rows — exactly the set
// callers of this hook expect (the farm-tenant master rows aren't part
// of any packhouse's grower directory).
// Call after any grower / PUC / block mutation in /admin/growers so
// every downstream consumer (Deliveries' grower picker via useGrowers,
// PUC pickers via usePucs, block dropdowns via useBlocks, /admin tree
// via useGrowerTree, plus the per-feature grower dropdowns on Invoice,
// TransportInvoice, BinInventory etc.) refetches in parallel.
//
// History: first version only invalidated ['grower-tree']; 2026-05-20
// fix added ['growers'] / ['pucs'] / ['blocks']. Still missed every
// page-specific ['growers-for-*'] dropdown — operator reported that
// newly-added growers were still missing from the Invoice / Bin Loan
// pickers. This version uses a predicate so a new page-scoped grower
// dropdown is invalidated by default; only per-grower DETAIL keys
// (length > 1, like ['grower-detail', id]) are deliberately skipped
// because they're not affected by adding a different grower.
export function invalidateGrowerData(qc: import('@tanstack/react-query').QueryClient) {
  // PUC + block lists are grower-derived but their keys don't contain
  // "grower", so they need explicit invalidation.
  qc.invalidateQueries({ queryKey: ['pucs']   })
  qc.invalidateQueries({ queryKey: ['blocks'] })
  // Predicate sweep: any single-element queryKey whose label mentions
  // "grower". Catches ['growers'], ['grower-tree'], every
  // ['growers-for-*'] per-feature dropdown, plus ['compliance-growers'].
  // Length === 1 excludes per-grower detail queries.
  qc.invalidateQueries({
    predicate: q =>
      q.queryKey.length === 1
      && typeof q.queryKey[0] === 'string'
      && /grower/i.test(q.queryKey[0] as string),
  })
  // Tenant-keyed grower lists fall OUTSIDE the length-1 sweep (their suffix is
  // a uuid, indistinguishable from a per-grower detail id) — invalidate them
  // by prefix explicitly. Add a line here when tenant-keying another one.
  qc.invalidateQueries({ queryKey: ['farm-compliance-docs-growers'] })
}

export function useGrowers() {
  return useQuery({
    queryKey: ['growers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('growers_resolved')
        .select('*')
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('name')
      if (error) throw error
      return data as Grower[]
    },
    staleTime: 5 * 60_000,
  })
}

// PUCs for a specific grower (excludes soft-deleted)
export function usePucs(growerId: string | null) {
  return useQuery({
    queryKey: ['pucs', growerId],
    enabled: !!growerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pucs')
        .select('*')
        .eq('grower_id', growerId!)
        .is('deleted_at', null)
        .order('puc_code')
      if (error) throw error
      return data as Puc[]
    },
    staleTime: 5 * 60_000,
  })
}

// Blocks for a specific PUC (excludes soft-deleted)
export function useBlocks(pucId: string | null) {
  return useQuery({
    queryKey: ['blocks', pucId],
    enabled: !!pucId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blocks')
        .select('*')
        .eq('puc_id', pucId!)
        .is('deleted_at', null)
        .order('block_name')
      if (error) throw error
      return data as Block[]
    },
    staleTime: 5 * 60_000,
  })
}

interface GrowerBlock {
  block_id:   string
  puc_id:     string
  block_name: string
  variety:    string | null
  puc_code:   string | null
  /** 'farm' = read live from an ACTIVE-linked farm tenant (not mirrored on the
   *  packhouse side); 'packhouse' = a packhouse-owned block row. */
  origin:     'packhouse' | 'farm'
}

// Block-picker source for delivery intake: a grower's own packhouse blocks PLUS
// its ACTIVE-linked farm's orchard blocks read LIVE (origin='farm'), deduped
// against any already mirrored down. Scoped by grower (not by a selected PUC)
// because the berry intake keeps PUC as optional free-text — unlike the
// PUC-scoped, packhouse-only useBlocks sibling above.
//
// Backed by the grower_blocks_for_intake SECURITY DEFINER RPC because table RLS
// does NOT expose a linked farm's grower/puc/block rows to the packhouse: the
// LINKED_EXISTING dedup path sets up inheritance but never mirrors blocks down,
// and current_packhouse_linked_farm_tenant_ids() is wired into SECDEF readers
// (e.g. get_linked_farm_estimates), not the blocks/pucs/growers SELECT policies.
// A plain blocks read therefore misses every inherited block — e.g. Anchor
// Fruits' "Sugar Blue" has 0 packhouse blocks but 16 live from the farm app.
// The RPC is fail-closed: caller must own the grower; farm rows only via an
// ACTIVE link (consent) — live mirror, never copy (Apple-ecosystem north star).
export function useGrowerBlocks(growerId: string | null) {
  return useQuery({
    queryKey: ['grower-blocks', growerId],
    enabled: !!growerId,
    queryFn: async (): Promise<GrowerBlock[]> => {
      const { data, error } = await supabase.rpc('grower_blocks_for_intake', { p_grower_id: growerId })
      if (error) throw error
      return ((data ?? []) as GrowerBlock[])
        .slice()
        .sort((a, b) => a.block_name.localeCompare(b.block_name))
    },
    staleTime: 5 * 60_000,
  })
}

// Full grower tree — used on the Growers management page. Filters
// soft-deleted growers and PUCs so they don't show in the tree at all.
//
// Stage 1 of grower inheritance (mig 270/271): merge `growers` (tree
// shape) + `growers_resolved` (resolved contact + source metadata).
//
// Stage 3 (mig 272): when a packhouse-side grower is linked to a farm
// tenant, ALSO fetch the farm-tenant master grower's pucs + blocks
// and attach them as `inherited_pucs` on the row. The packhouse UI
// renders these alongside the packhouse-owned pucs with a "from farm
// app" badge. Read-only on the packhouse side; the farm app's
// /farm/orchard page is where they're actually edited.
//
// We filter to packhouse-side rows only — every packhouse-side row in
// current production has farm_tenant_id IS NULL, every farm-master row
// has farm_tenant_id IS NOT NULL — so mig 270's extended RLS doesn't
// surface farm-tenant master rows as duplicate top-level grower rows
// in the tree. (Pre-mig-535 this was packhouse_tenant_id IS NOT NULL;
// the form here survives the Stage D drop of that column.)
export function useGrowerTree() {
  return useQuery({
    queryKey: ['grower-tree'],
    // Match the sibling hooks (useGrowers/usePucs/useBlocks): the tree is
    // reference data that changes rarely, but it's the heaviest of the set
    // (4-5 round-trips). Without staleTime the whole waterfall re-ran on every
    // window-focus / remount; 5 min serves it from cache between edits (which
    // invalidate ['grower-tree'] explicitly).
    staleTime: 5 * 60_000,
    // The tree fans out into 4–5 server roundtrips (growers + resolved
    // overlay + master pucs/blocks + pucs_resolved + blocks_resolved).
    // Any one of them failing on a cold mount (auth-token lock race,
    // tenant claim still warming after a switcher reload, transient
    // 5xx) used to silently render an empty page until the operator
    // hit reload. Two retries with backoff lets the query self-heal
    // through the warm-up window.
    retry: 3,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4_000),
    queryFn: async () => {
      // 1. Packhouse-owned grower tree.
      const { data, error } = await supabase
        .from('growers')
        .select(`
          *,
          pucs (
            *,
            blocks (*)
          )
        `)
        .is('farm_tenant_id', null)
        .is('deleted_at', null)
        .order('name')
      if (error) throw error
      const packhouseGrowers = (data ?? []) as (Grower & { pucs: (Puc & { blocks: Block[] })[] })[]

      // 2. Resolution metadata (contact / address inheritance — Stage 1).
      // Filter to the packhouse grower IDs we actually have — RLS already
      // scopes server-side, but the explicit IN keeps multi-packhouse users
      // from paying the network cost of every grower they can read.
      const packhouseGrowerIds = packhouseGrowers.map(g => g.grower_id)
      const { data: resolvedRows, error: rErr } = packhouseGrowerIds.length === 0
        ? { data: [], error: null }
        : await supabase
            .from('growers_resolved')
            .select(`
              grower_id, linked_farm_tenant_id,
              contact_name, contact_phone, contact_email, whatsapp_number,
              address, notes, bin_tare_kg,
              vat_number, ggn_number, company_registration,
              address_line1, address_line2, city, postcode, country,
              contact_name_source, contact_phone_source, contact_email_source,
              whatsapp_number_source, address_source, notes_source, bin_tare_kg_source,
              vat_number_source, ggn_number_source, company_registration_source,
              address_line1_source, address_line2_source, city_source, postcode_source, country_source,
              master_contact_name, master_contact_phone, master_contact_email,
              master_whatsapp_number, master_address, master_notes, master_bin_tare_kg,
              master_vat_number, master_ggn_number, master_company_registration,
              master_address_line1, master_address_line2, master_city, master_postcode, master_country,
              contact_name_override, contact_phone_override, contact_email_override,
              whatsapp_number_override, address_override, notes_override, bin_tare_kg_override,
              vat_number_override, ggn_number_override, company_registration_override,
              address_line1_override, address_line2_override, city_override, postcode_override, country_override
            `)
            .in('grower_id', packhouseGrowerIds)
      if (rErr) throw rErr
      const resolvedById = new Map((resolvedRows ?? []).map((r: any) => [r.grower_id, r]))

      // 3. Farm-tenant master grower pucs/blocks for the linked subset.
      // Via the linked_farm_master_trees SECDEF RPC, NOT a plain select:
      // table RLS has no packhouse-direction arm (growers_read_scoped's
      // current_tenant_grower_ids() is farm-direction only), so a direct
      // growers/pucs/blocks read of a LINKED_EXISTING farm returned 0 rows
      // and the tree silently showed the mirrored PUC with no blocks
      // (Anchor Fruits, 2026-07-10). The RPC is gated on ACTIVE-link
      // consent server-side and pre-filters soft-deleted pucs/blocks.
      const linkedFarmTenantIds = Array.from(new Set(
        (resolvedRows ?? []).map((r: any) => r.linked_farm_tenant_id).filter(Boolean) as string[]
      ))
      // Accumulator, NOT a "last wins" map. If a farm tenant ever has more
      // than one master grower row (unusual today but allowed by the schema),
      // we must merge their PUCs rather than overwrite — otherwise the
      // second master's pucs silently replace the first's and only one
      // half of the orchard surfaces in the link picker. Same shape of
      // bug as the 2026-05-13 TJ van der Merwe cross-tenant duplicate.
      const inheritedPucsByLinkedTenantId = new Map<string, (Puc & { blocks: Block[] })[]>()
      if (linkedFarmTenantIds.length > 0) {
        const { data: masterRows, error: mErr } = await supabase.rpc('linked_farm_master_trees')
        if (mErr) throw mErr
        for (const m of masterRows ?? []) {
          const pucs = (m.pucs ?? []) as unknown as (Puc & { blocks: Block[] })[]
          const existing = inheritedPucsByLinkedTenantId.get(m.farm_tenant_id) ?? []
          inheritedPucsByLinkedTenantId.set(m.farm_tenant_id, existing.concat(pucs))
        }
      }

      // 4. Stage 3c: resolve packhouse-side blocks + pucs through their
      // own master link (mig 273/274). For each packhouse-side block /
      // puc, blocks_resolved / pucs_resolved gives COALESCE(override,
      // master, fallback) + per-field source flags + the master raw
      // values. We fetch the resolved rows separately and overlay them
      // onto the tree (same pattern as growers_resolved above).
      const packhousePucIds   = packhouseGrowers.flatMap(g => (g.pucs ?? []).map(p => p.puc_id))
      const packhouseBlockIds = packhouseGrowers.flatMap(g => (g.pucs ?? []).flatMap(p => (p.blocks ?? []).map(b => b.block_id)))
      const [pucsRes, blocksRes] = await Promise.all([
        packhousePucIds.length > 0
          ? supabase.from('pucs_resolved').select('*').in('puc_id', packhousePucIds)
          : Promise.resolve({ data: [], error: null as any }),
        packhouseBlockIds.length > 0
          ? supabase.from('blocks_resolved').select('*').in('block_id', packhouseBlockIds)
          : Promise.resolve({ data: [], error: null as any }),
      ])
      if (pucsRes.error)   throw pucsRes.error
      if (blocksRes.error) throw blocksRes.error
      const pucResolvedById   = new Map((pucsRes.data   ?? []).map((p: any) => [p.puc_id,   p]))
      const blockResolvedById = new Map((blocksRes.data ?? []).map((b: any) => [b.block_id, b]))

      return packhouseGrowers.map(g => {
        const r = resolvedById.get(g.grower_id)
        const merged: typeof g = r ? { ...g, ...r } : g
        // The packhouse-side mirrors point at farm masters via
        // master_puc_id / master_block_id. The previous dedupe hid an
        // entire inherited PUC the moment its puc_id appeared on a
        // packhouse mirror — but a farm tenant can add blocks under that
        // same PUC AFTER the original packhouse promotion, and those
        // farm-only blocks have no packhouse mirror yet. Hiding the whole
        // PUC made them invisible to the packhouse operator. Block-level
        // dedupe keeps the inherited PUC visible only when it carries
        // farm-only blocks that aren't already represented as mirrors.
        const mirroredMasterPucIds = new Set(
          ((merged.pucs ?? []) as Array<{ master_puc_id?: string | null }>)
            .map(p => p.master_puc_id)
            .filter(Boolean) as string[]
        )
        const mirroredMasterBlockIds = new Set(
          ((merged.pucs ?? []) as Array<{ blocks?: Array<{ master_block_id?: string | null }> }>)
            .flatMap(p => (p.blocks ?? []).map(b => b.master_block_id))
            .filter(Boolean) as string[]
        )
        const inheritedPucs = merged.linked_farm_tenant_id
          ? (inheritedPucsByLinkedTenantId.get(merged.linked_farm_tenant_id) ?? [])
              .map((ip: any) => ({
                ...ip,
                blocks: ((ip.blocks ?? []) as Array<{ block_id: string }>)
                  .filter(b => !mirroredMasterBlockIds.has(b.block_id)),
              }))
              .filter((ip: any) => {
                // Drop the inherited PUC iff it's a mirrored PUC AND has
                // nothing the packhouse hasn't already mirrored. An
                // unmirrored PUC (rare) keeps showing as-is so the
                // operator can decide whether to mirror.
                if (mirroredMasterPucIds.has(ip.puc_id) && ip.blocks.length === 0) return false
                return true
              })
          : []
        return {
          ...merged,
          pucs: (merged.pucs ?? [])
            .filter((p: any) => !p.deleted_at)
            .map(p => {
              const pr = pucResolvedById.get(p.puc_id) as Partial<Puc> | undefined
              const mergedPuc = pr ? { ...p, ...pr } : p
              return {
                ...mergedPuc,
                blocks: (p.blocks ?? [])
                  .filter((b: any) => !b.deleted_at)
                  .map((b: Block) => {
                    const br = blockResolvedById.get(b.block_id) as Partial<Block> | undefined
                    return br ? { ...b, ...br } : b
                  }),
              }
            }),
          inherited_pucs: inheritedPucs,
        }
      })
    },
  })
}
